import CopyableId from "@/components/common/CopyableId";
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, ClipboardCheck, FileEdit, CheckCircle2, ChevronRight, Clock, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import StatusBadge from "@/components/common/StatusBadge";
import { api, formatApiError } from "@/lib/api";

function Kpi({ icon: Icon, label, value, tone }) {
  return (
    <div className="hah-card p-4 flex items-center gap-3" data-testid={`cpq-kpi-${label}`}>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${tone}`}><Icon className="w-5 h-5" /></div>
      <div><div className="text-2xl font-semibold text-[#1F2937] leading-none">{value}</div><div className="text-xs text-[#6B7280] mt-1">{label}</div></div>
    </div>
  );
}

function PlanCard({ plan, onOpen }) {
  const prog = plan.progress || {};
  return (
    <button onClick={onOpen} data-testid={`cpq-card-${plan.mrn}`} className="w-full text-left hah-card p-4 flex items-center gap-4 hover:border-[#0B7E8C] hover:shadow-sm transition-all">
      <div className="w-10 h-10 rounded-lg bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center shrink-0"><ClipboardCheck className="w-5 h-5" /></div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-[#1F2937]">{plan.patient_name}</span>
          <span className="font-mono text-xs text-[#0B7E8C]"><CopyableId value={plan.mrn} kind="MRN" /></span>
          <StatusBadge status={plan.status} />
          {plan.version > 0 && <span className="text-[11px] text-[#6B7280]">v{plan.version}</span>}
        </div>
        <div className="text-xs text-[#6B7280] mt-1 truncate">{plan.primary_diagnosis || "—"}</div>
        <div className="text-[11px] text-[#94A3B8] mt-1 flex items-center gap-3 flex-wrap">
          {plan.status === "pending" && plan.age_days != null && (
            <span className={`inline-flex items-center gap-1 ${plan.age_days >= 2 ? "text-[#B45309]" : ""}`}><Clock className="w-3 h-3" />Pending {plan.age_days}d</span>
          )}
          {plan.status === "published" && <span>Discharge criteria {prog.discharge_criteria_met}/{prog.discharge_criteria_total} met</span>}
          {plan.episode_ref && <span>{plan.episode_ref}</span>}
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-[#CBD5E1] shrink-0" />
    </button>
  );
}

export default function CarePlanQueue() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async (status) => {
    setLoading(true);
    try {
      const sts = status === "pending" ? "pending,draft" : status;
      const [s, list] = await Promise.all([
        api.get("/care-plans/summary"),
        api.get(`/care-plans?statuses=${sts}`),
      ]);
      setSummary(s.data); setItems(list.data.items || []);
    } catch (e) {
      if (e?.response?.status === 403) setDenied(true);
      else toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(tab); }, [tab, load]);

  if (denied) {
    return (
      <div data-testid="care-plan-queue">
        <PageHeader title="Care Plans" subtitle="Structured care plans guiding patients to discharge." />
        <div className="hah-card p-10 text-center" data-testid="cpq-denied">
          <AlertCircle className="w-10 h-10 text-[#CBD5E1] mx-auto mb-2" />
          <p className="text-sm text-[#6B7280]">You don't have access to care plans.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="care-plan-queue">
      <PageHeader title="Care Plans" subtitle="Create and manage structured care plans guiding patients to discharge." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Kpi icon={AlertCircle} label="Pending" value={summary?.pending ?? "—"} tone="bg-[#FEF3C7] text-[#B45309]" />
        <Kpi icon={FileEdit} label="In draft" value={summary?.draft ?? "—"} tone="bg-[#E0E7FF] text-[#4338CA]" />
        <Kpi icon={CheckCircle2} label="Published" value={summary?.published ?? "—"} tone="bg-[#D1FAE5] text-[#059669]" />
        <Kpi icon={ClipboardList} label="Assigned to me" value={summary?.mine_pending ?? "—"} tone="bg-[#EAF8FA] text-[#0B7E8C]" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#F5F7FA]">
          <TabsTrigger value="pending" data-testid="cpq-tab-pending">Pending / Draft</TabsTrigger>
          <TabsTrigger value="published" data-testid="cpq-tab-published">Published</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4 space-y-2">
          {loading ? <div className="text-sm text-[#6B7280] py-6">Loading…</div>
            : items.length === 0 ? <div className="hah-card p-8 text-center text-sm text-[#94A3B8]">No care plans here.</div>
            : items.map((p) => <PlanCard key={p.id} plan={p} onOpen={() => navigate(`/care-plans/${p.id}`)} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
